/**
 * Member Work Compliance Tab
 * Displays detailed Medicaid work compliance status, hours tracking,
 * compliance history, alerts, and incentives for a member.
 */

import React, { useState, useEffect } from 'react';
import {
  Loader2,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Award,
  RefreshCw,
  Info,
  XCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import workComplianceApi, {
  ComplianceDashboard,
  MonthlyCompliance,
} from '@/services/api/workCompliance';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface MemberWorkComplianceTabProps {
  memberId: number;
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export default function MemberWorkComplianceTab({ memberId }: MemberWorkComplianceTabProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [dashboard, setDashboard] = useState<ComplianceDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, [memberId]);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await workComplianceApi.getMemberDashboard(memberId);
      setDashboard(data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('no_enrollment');
      } else {
        setError(err.response?.data?.detail || err.message || t('members.failedToLoadComplianceData', 'Failed to load compliance data'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error === 'no_enrollment') {
    return (
      <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <Info className="h-4 w-4" />
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          {t('members.noMedicaidWorkEnrollment', 'No active Medicaid enrollment with work requirements found for this member. Enroll the member in a Medicaid program to enable work compliance tracking.')}
        </AlertDescription>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!dashboard) return null;

  const hoursPercent = Math.min(100, (dashboard.current_month_hours / 80) * 100);

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{t('members.workComplianceDashboard', 'Work Compliance Dashboard')}</h2>
        <Button variant="outline" size="sm" onClick={loadDashboard}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('members.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Active Alerts */}
      {dashboard.active_alerts.length > 0 && (
        <div className="flex flex-col gap-2">
          {dashboard.active_alerts.map((alert) => (
            <Alert
              key={alert.id}
              variant={alert.severity === 'HIGH' || alert.severity === 'CRITICAL' ? 'destructive' : undefined}
              className={
                alert.severity === 'MEDIUM'
                  ? 'border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-200'
                  : alert.severity === 'LOW'
                  ? 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-200'
                  : undefined
              }
            >
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>{alert.title}</strong>
                <br />
                {alert.message}
                {alert.deadline_date && (
                  <span className="text-xs ml-2">
                    ({t('members.deadlineLabel', 'Deadline')}: {fmt.short(alert.deadline_date)})
                  </span>
                )}
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Current Month Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Hours Progress */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                {MONTH_NAMES[dashboard.current_month - 1]} {dashboard.current_year} {t('members.hours', 'Hours')}
              </h3>
            </div>
            <div className="text-3xl font-bold mb-2">
              {dashboard.current_month_hours}
              <span className="text-lg text-gray-400 font-normal"> {t('members.of80Hrs', '/ 80 hrs')}</span>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
              <div
                className={`h-3 rounded-full transition-all ${
                  hoursPercent >= 100
                    ? 'bg-green-500'
                    : hoursPercent >= 50
                    ? 'bg-blue-500'
                    : hoursPercent >= 25
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                }`}
                style={{ width: `${hoursPercent}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>{Math.round(hoursPercent)}{t('members.percentComplete', '% complete')}</span>
              {dashboard.hours_needed > 0 && (
                <span>{dashboard.hours_needed} {t('members.hrsNeeded', 'hrs needed')}</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Compliance Status */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              {dashboard.current_month_compliant ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              )}
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                {t('members.complianceStatus', 'Compliance Status')}
              </h3>
            </div>
            <div className="mb-2">
              <Badge
                className={`text-sm px-3 py-1 ${
                  dashboard.current_month_compliant
                    ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/50 dark:text-green-200'
                    : dashboard.current_month_status === 'NOT_SUBMITTED'
                    ? 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200'
                    : 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/50 dark:text-yellow-200'
                }`}
              >
                {dashboard.current_month_compliant
                  ? t('members.compliant', 'Compliant')
                  : dashboard.current_month_status === 'NOT_SUBMITTED'
                  ? t('members.notSubmitted', 'Not Submitted')
                  : t('members.nonCompliant', 'Non-Compliant')}
              </Badge>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium">{dashboard.days_until_deadline}</span> {t('members.daysUntilDeadline', 'days until deadline')}
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {t('members.dueLabel', 'Due')}: {fmt.short(dashboard.deadline_date)}
            </div>
          </CardContent>
        </Card>

        {/* Streak & Incentives */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Award className="h-5 w-5 text-purple-600" />
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                {t('members.incentivesAndStreak', 'Incentives & Streak')}
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('members.compliantMonths', 'Compliant Months')}</span>
                <span className="text-lg font-bold text-purple-600">
                  {dashboard.consecutive_compliant_months}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('members.pointsEarned', 'Points Earned')}</span>
                <span className="text-sm font-semibold">
                  {dashboard.total_points_earned}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('members.incentivesEarned', 'Incentives Earned')}</span>
                <span className="text-sm font-semibold text-green-600">
                  ${dashboard.total_incentives_earned.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('members.pendingPayout', 'Pending Payout')}</span>
                <span className="text-sm font-semibold text-yellow-600">
                  ${dashboard.incentives_pending.toFixed(2)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 6-Month Compliance History */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold">{t('members.sixMonthComplianceHistory', '6-Month Compliance History')}</h3>
            {dashboard.pending_verifications > 0 && (
              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/50 dark:text-yellow-200 ml-2">
                {dashboard.pending_verifications} {t('members.pendingVerification', 'pending verification')}
              </Badge>
            )}
          </div>

          {dashboard.last_6_months_compliance.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('members.noComplianceHistory', 'No compliance history yet. Work hours will appear here after the first monthly submission.')}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {dashboard.last_6_months_compliance.map((month: MonthlyCompliance) => (
                <div
                  key={`${month.year}-${month.month}`}
                  className={`rounded-lg border p-3 text-center ${
                    month.compliant
                      ? 'border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                      : month.verification_status === 'PENDING'
                      ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20'
                      : 'border-red-200 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
                  }`}
                >
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {MONTH_NAMES[month.month - 1]} {month.year}
                  </div>
                  <div className="text-lg font-bold mt-1">
                    {month.total_hours}
                    <span className="text-xs font-normal text-gray-400"> {t('members.hrs', 'hrs')}</span>
                  </div>
                  <div className="mt-1">
                    {month.compliant ? (
                      <CheckCircle className="h-4 w-4 text-green-600 mx-auto" />
                    ) : month.verification_status === 'PENDING' ? (
                      <Clock className="h-4 w-4 text-yellow-600 mx-auto" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600 mx-auto" />
                    )}
                  </div>
                  <div className="text-[10px] mt-1 text-gray-400">
                    {month.verification_status === 'APPROVED'
                      ? t('members.verified', 'Verified')
                      : month.verification_status === 'PENDING'
                      ? t('members.pending', 'Pending')
                      : month.verification_status === 'REJECTED'
                      ? t('members.rejected', 'Rejected')
                      : month.verification_status}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
